import {
  AssistantUploadKind,
  type AssistantUpload,
} from '@xtreed/academy-shared';
import { assistantService } from '@/services/assistant.service';
import { useAssistantStore } from '@/stores/assistant';

const MAX_RESUME_ATTEMPTS = 3;
const files = new Map<string, File>();
const listeners = new Set<() => void>();

export function onUploadSettled(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function settled() {
  listeners.forEach((listener) => listener());
}

export function uploadKindFor(file: File): AssistantUploadKind {
  if (file.type.startsWith('video/')) return AssistantUploadKind.VIDEO;
  if (file.type.startsWith('image/')) return AssistantUploadKind.IMAGE;
  return AssistantUploadKind.MATERIAL;
}

function setState(
  id: string,
  patch: Parameters<
    ReturnType<typeof useAssistantStore.getState>['setUploadState']
  >[1],
) {
  useAssistantStore.getState().setUploadState(id, patch);
}

function put(
  url: string,
  file: File,
  offset: number,
  onProgress: (loaded: number) => void,
): Promise<number> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    if (offset > 0) {
      xhr.setRequestHeader(
        'Content-Range',
        `bytes ${offset}-${file.size - 1}/${file.size}`,
      );
    }
    xhr.upload.onprogress = (event) => onProgress(offset + event.loaded);
    xhr.onload = () => resolve(xhr.status);
    xhr.onerror = () => reject(new Error('NETWORK_ERROR'));
    xhr.onabort = () => reject(new Error('ABORTED'));
    xhr.send(offset > 0 ? file.slice(offset) : file);
  });
}

function queryOffset(url: string, size: number): Promise<number | 'done'> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Range', `bytes */${size}`);
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) return resolve('done');
      const range = xhr.getResponseHeader('Range');
      const match = range?.match(/bytes=0-(\d+)/);
      resolve(match ? Number(match[1]) + 1 : 0);
    };
    xhr.onerror = () => resolve(0);
    xhr.send();
  });
}

async function send(id: string, url: string, file: File): Promise<void> {
  let offset = 0;
  for (let attempt = 0; attempt <= MAX_RESUME_ATTEMPTS; attempt++) {
    try {
      const status = await put(url, file, offset, (loaded) =>
        setState(id, { progress: loaded / file.size }),
      );
      if (status === 200 || status === 201) return;
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw new Error(`HTTP_${status}`);
      }
    } catch (error) {
      if (attempt === MAX_RESUME_ATTEMPTS) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    const next = await queryOffset(url, file.size);
    if (next === 'done') return;
    offset = next;
  }
  throw new Error('UPLOAD_FAILED');
}

async function run(id: string, url: string, file: File) {
  setState(id, { progress: 0, error: null, needsFile: false });
  try {
    await send(id, url, file);
    setState(id, { progress: 1 });
    await assistantService.completeUpload(id);
    files.delete(id);
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'UPLOAD_FAILED';
    setState(id, { error: reason });
    await assistantService.failUpload(id, reason).catch(() => undefined);
  } finally {
    settled();
  }
}

export async function startUpload(
  conversationId: string,
  file: File,
): Promise<AssistantUpload> {
  const session = await assistantService.createUpload({
    conversationId,
    kind: uploadKindFor(file),
    filename: file.name,
    size: file.size,
    mimeType: file.type || 'application/octet-stream',
  });
  files.set(session.upload.id, file);
  if (session.sessionUrl) void run(session.upload.id, session.sessionUrl, file);
  return session.upload;
}

export async function retryUpload(id: string, replacement?: File) {
  const file = replacement ?? files.get(id);
  const session = await assistantService.retryUpload(id);
  if (!session.sessionUrl) {
    settled();
    return;
  }
  if (!file) {
    setState(id, { needsFile: true, error: null });
    return;
  }
  files.set(id, file);
  await run(id, session.sessionUrl, file);
}

export function hasLocalFile(id: string) {
  return files.has(id);
}
